import { useState, useCallback, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { authFetch } from '../lib/authFetch';

/**
 * useSavedSearches — manages a signed-in user's saved Live Feed searches
 * (named filter sets), persisted in the saved_searches table via /api/saved-searches.
 *
 * Module-level shared state so the filter bar (which saves) and the sidebar
 * (which lists/applies/deletes) stay in sync without a refresh — same pattern
 * as useFollows.
 */

let _searches = [];
let _loaded = false;
let _loadingPromise = null;
let _userId = null;
const _listeners = new Set();

// Cross-component "apply a saved search" channel (sidebar → Live Feed) without
// threading through App. The sidebar calls requestApplySavedSearch(filters);
// the Live Feed subscribes via useSavedSearchApply and sets its filter state.
const _applyListeners = new Set();
let _pendingApply = null;
export function requestApplySavedSearch(filters) {
  if (_applyListeners.size > 0) {
    _applyListeners.forEach(fn => { try { fn(filters); } catch (e) { /* ignore */ } });
  } else {
    // Live Feed not mounted yet (clicked from another tab) — buffer it; the
    // subscriber consumes it on mount after navigation lands on the feed.
    _pendingApply = filters;
  }
}
export function useSavedSearchApply(cb) {
  useEffect(() => {
    _applyListeners.add(cb);
    if (_pendingApply) { const p = _pendingApply; _pendingApply = null; cb(p); }
    return () => _applyListeners.delete(cb);
  }, [cb]);
}

// Serialize/deserialize the Live Feed filter object (Sets ⇄ arrays for JSON).
export function serializeFilters(f) {
  return {
    search: f.search || '',
    sectors: [...(f.sectors || [])],
    geos: [...(f.geos || [])],
    cps: [...(f.cps || [])],
    dealTypes: [...(f.dealTypes || [])],
    stages: [...(f.stages || [])],
    valueBucket: f.valueBucket || 'all',
    dateWindow: f.dateWindow || 'all',
    followingOnly: !!f.followingOnly,
  };
}
export function deserializeFilters(f) {
  f = f || {};
  return {
    search: f.search || '',
    sectors: new Set(f.sectors || []),
    geos: new Set(f.geos || []),
    cps: new Set(f.cps || []),
    dealTypes: new Set(f.dealTypes || []),
    stages: new Set(f.stages || []),
    valueBucket: f.valueBucket || 'all',
    dateWindow: f.dateWindow || 'all',
    followingOnly: !!f.followingOnly,
  };
}

function _set(next) {
  _searches = next;
  _listeners.forEach(fn => fn([..._searches]));
}

export function useSavedSearches() {
  const { user, isSignedIn } = useUser();
  const [searches, setSearches] = useState(_searches);

  useEffect(() => {
    _listeners.add(setSearches);
    if (_loaded) setSearches([..._searches]);
    return () => _listeners.delete(setSearches);
  }, []);

  useEffect(() => {
    if (!isSignedIn || !user?.id) {
      if (_loaded || _searches.length > 0) {
        _searches = []; _loaded = false; _userId = null; _loadingPromise = null;
        _listeners.forEach(fn => fn([]));
      }
      return;
    }
    if (_userId && _userId !== user.id) { _searches = []; _loaded = false; _loadingPromise = null; }
    if (_loaded && _userId === user.id) return;
    if (_loadingPromise) return;

    _userId = user.id;
    _loadingPromise = authFetch(`/api/saved-searches?userId=${user.id}`)
      .then(r => r.json())
      .then(result => { _searches = result.searches || []; _loaded = true; _listeners.forEach(fn => fn([..._searches])); })
      .catch(err => console.error('Error loading saved searches:', err))
      .finally(() => { _loadingPromise = null; });
  }, [isSignedIn, user?.id]);

  const save = useCallback(async (name, filters) => {
    if (!isSignedIn || !user?.id) return false;
    try {
      const res = await authFetch('/api/saved-searches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, name, filters }),
      });
      const result = await res.json();
      if (result.search) _set([result.search, ..._searches]);
      return true;
    } catch (err) {
      console.error('Error saving search:', err);
      return false;
    }
  }, [isSignedIn, user?.id]);

  const remove = useCallback(async (id) => {
    if (!isSignedIn || !user?.id) return false;
    const prev = _searches;
    _set(_searches.filter(s => s.id !== id)); // optimistic
    try {
      await authFetch('/api/saved-searches', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, id }),
      });
      return true;
    } catch (err) {
      console.error('Error deleting saved search:', err);
      _set(prev); // revert
      return false;
    }
  }, [isSignedIn, user?.id]);

  return { searches, save, remove, isSignedIn };
}
